/**
 * Portal Engine lazy loader — the recommended entry for external consumers.
 *
 * A .NET view references a single module script:
 *
 *   <script type="module" src="~/portal-engine/portal-engine.js"></script>
 *
 * This script ships almost nothing itself. It scans the page for Portal Engine
 * custom elements (`pe-*`) and dynamically imports only the modules actually
 * used — each component is a separate chunk, and heavy dependencies (Chart.js)
 * load only when a chart/sparkline is on the page. A MutationObserver picks up
 * elements added later by SPA navigation, htmx swaps, or partial renders.
 *
 * Each component module self-registers via Lit's @customElement on import, so
 * discovered-but-undefined elements upgrade automatically once their chunk
 * arrives.
 */
import './styles/tailwind.css';

type ChunkLoader = () => Promise<unknown>;

/** tag → dynamic import of the chunk that defines it. */
const registry: Record<string, ChunkLoader> = {
  'pe-dashboard': () => import('./components/layout/pe-dashboard.js'),
  'pe-panel': () => import('./components/layout/pe-panel.js'),
  'pe-toggle': () => import('./components/toggle/pe-toggle.js'),
  'pe-input': () => import('./components/inputs/pe-input.js'),
  'pe-select': () => import('./components/inputs/pe-select.js'),
  'pe-checkbox': () => import('./components/inputs/pe-checkbox.js'),
  'pe-data-table': () => import('./components/table/pe-data-table.js'),
  'pe-chart': () => import('./components/charts/pe-chart.js'),
  'pe-sparkline': () => import('./components/charts/pe-sparkline.js'),
  'pe-stat': () => import('./components/charts/pe-stat.js'),

  // Phase 0 — primitives
  'pe-icon': () => import('./components/primitives/pe-icon.js'),
  'pe-button': () => import('./components/primitives/pe-button.js'),
  'pe-button-group': () => import('./components/primitives/pe-button-group.js'),
  'pe-badge': () => import('./components/primitives/pe-badge.js'),
  'pe-skeleton': () => import('./components/primitives/pe-skeleton.js'),
  'pe-empty-state': () => import('./components/primitives/pe-empty-state.js'),
  'pe-spinner': () => import('./components/primitives/pe-spinner.js'),
  'pe-form-field': () => import('./components/primitives/pe-form-field.js'),
  'pe-pagination': () => import('./components/primitives/pe-pagination.js'),

  // Phase 1 — overlays & structure
  'pe-modal': () => import('./components/overlays/pe-modal.js'),
  'pe-drawer': () => import('./components/overlays/pe-drawer.js'),
  'pe-tabs': () => import('./components/overlays/pe-tabs.js'),
  'pe-tab': () => import('./components/overlays/pe-tab.js'),
  'pe-accordion': () => import('./components/overlays/pe-accordion.js'),
  'pe-accordion-item': () => import('./components/overlays/pe-accordion-item.js'),
  'pe-menu': () => import('./components/overlays/pe-menu.js'),
  'pe-menu-item': () => import('./components/overlays/pe-menu-item.js'),
  'pe-tooltip': () => import('./components/overlays/pe-tooltip.js'),
  'pe-popover': () => import('./components/overlays/pe-popover.js'),
  'pe-notifications': () => import('./components/overlays/pe-notifications.js'),

  // Phase 2 — feedback & status
  'pe-toast': () => import('./components/feedback/pe-toast.js'),
  'pe-alert': () => import('./components/feedback/pe-alert.js'),
  'pe-progress': () => import('./components/feedback/pe-progress.js'),

  // Phase 3 — form completion
  'pe-textarea': () => import('./components/inputs/pe-textarea.js'),
  'pe-radio-group': () => import('./components/inputs/pe-radio-group.js'),
  'pe-date': () => import('./components/inputs/pe-date.js'),
  'pe-number': () => import('./components/inputs/pe-number.js'),
  'pe-file': () => import('./components/inputs/pe-file.js'),
  'pe-search': () => import('./components/inputs/pe-search.js'),
  'pe-combobox': () => import('./components/inputs/pe-combobox.js'),
  'pe-slider': () => import('./components/inputs/pe-slider.js'),
  'pe-tag-input': () => import('./components/inputs/pe-tag-input.js'),

  // Phase 4 — data display
  'pe-list': () => import('./components/data/pe-list.js'),
  'pe-description-list': () => import('./components/data/pe-description-list.js'),
  'pe-timeline': () => import('./components/data/pe-timeline.js'),
  'pe-avatar': () => import('./components/data/pe-avatar.js'),
  'pe-avatar-group': () => import('./components/data/pe-avatar-group.js'),
  'pe-kpi-group': () => import('./components/data/pe-kpi-group.js'),
  'pe-tree': () => import('./components/data/pe-tree.js'),
  'pe-calendar': () => import('./components/data/pe-calendar.js'),

  // Phase 5 — app shell / navigation
  'pe-nav': () => import('./components/shell/pe-nav.js'),
  'pe-nav-tabs': () => import('./components/shell/pe-nav-tabs.js'),
  'pe-breadcrumbs': () => import('./components/shell/pe-breadcrumbs.js'),
  'pe-app-bar': () => import('./components/shell/pe-app-bar.js'),
  'pe-footer-bar': () => import('./components/shell/pe-footer-bar.js'),
  'pe-date-range': () => import('./components/shell/pe-date-range.js'),
  'pe-date-compare': () => import('./components/shell/pe-date-compare.js'),
  'pe-lens-bar': () => import('./components/shell/pe-lens-bar.js'),
  'pe-lens': () => import('./components/shell/pe-lens.js'),

  // Phase 6 — extended charts
  'pe-gauge': () => import('./components/charts/pe-gauge.js'),
  'pe-funnel': () => import('./components/charts/pe-funnel.js'),
  'pe-heatmap': () => import('./components/charts/pe-heatmap.js'),
};

/** Tags whose chunk import has started (deduplicates). */
const requested = new Set<string>();

/** Every Portal Engine tag this build knows how to load. */
export const knownTags: readonly string[] = Object.keys(registry);

/** Load the chunk for one tag (no-op if unknown or already requested). */
function loadTag(tag: string): void {
  const key = tag.toLowerCase();
  const loader = registry[key];
  if (!loader || requested.has(key)) return;
  requested.add(key);
  loader().catch((err) => {
    requested.delete(key); // allow a later retry (e.g. transient network fail)
    console.error(`[portal-engine] failed to load <${key}>`, err);
  });
}

/**
 * Scan a subtree and load chunks for any not-yet-defined Portal Engine element
 * found within it (including the root itself).
 */
function scan(root: ParentNode | Element): void {
  const self = (root as Element).localName;
  if (self && registry[self] && !customElements.get(self)) loadTag(self);
  for (const tag of knownTags) {
    if (requested.has(tag)) continue;
    if ((root as ParentNode).querySelector?.(`${tag}:not(:defined)`)) loadTag(tag);
  }
}

/** Eagerly load specific components (or all, when called with no args). */
export function preload(tags: readonly string[] = knownTags): void {
  for (const tag of tags) loadTag(tag);
}

/** Manually trigger a scan — rarely needed; the observer handles most cases. */
export function refresh(root: ParentNode | Element = document): void {
  scan(root);
}

function boot(): void {
  scan(document);
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE) scan(node as Element);
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
}

// `type=module` scripts are deferred, so the DOM is parsed by the time this
// runs; guard anyway for injection before parse completion.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
